/**
 * ==========================================================================
 *  RECORDED SAMPLES
 * ==========================================================================
 *  The game synthesises every effect (synth.ts) and lets the browser speak
 *  every line (Voice.ts), and it keeps doing exactly that for any cue this
 *  folder has no file for. A recording does not replace the system - it
 *  overrides one cue of it:
 *
 *      src/audio/samples/sfx/hannu-swipe.mp3   ->  the one-shot 'hannu-swipe'
 *      src/audio/samples/voice/hannu.m4a       ->  the spoken line 'hannu'
 *      src/audio/samples/loops/karaoke.mp3     ->  the loop 'karaoke'
 *
 *  Alternate takes are numbered: `hannu-swipe.2.mp3`, `hannu-swipe.3.mp3`.
 *  One take is chosen per play from the seeded audio PRNG, so a run still
 *  sounds the same every time it is replayed - the determinism rule in
 *  README.md covers the ear as well as the eye.
 *
 *  Files go through Vite rather than `public/`, which buys three things: a
 *  content-hashed filename (so the CDN's immutable cache headers apply), a
 *  build that fails if a file goes missing, and a startup that can *see* what
 *  it has - `unknownSamples()` names anything whose filename matches no cue,
 *  which is how a typo gets caught by a test instead of by silence at the LAN.
 * ==========================================================================
 */

import { LOOP_SOUNDS, ONE_SHOT_SOUNDS, type LoopSound, type OneShotSound } from './AudioManager';
import { SPOKEN_LINE_IDS } from '../config/dialogue';

/** A cue name - one-shot or loop - or a spoken line id prefixed with `voice:`. */
export type SampleId = OneShotSound | LoopSound | `voice:${string}`;

export interface SampleSpec {
  id: SampleId;
  /** 1 for `hannu.mp3`, 2 for `hannu.2.mp3`. Only used to keep takes ordered. */
  take: number;
  /** Hashed URL Vite resolved the file to. */
  url: string;
}

export interface SampleIndex {
  /** Cue -> every take of it, in take order. */
  byId: Map<SampleId, SampleSpec[]>;
  /** Files whose name matches no cue. Nothing plays these; they are a mistake. */
  unknown: string[];
}

const ONE_SHOT_NAMES: ReadonlySet<string> = new Set(ONE_SHOT_SOUNDS);
const VOICE_NAMES: ReadonlySet<string> = new Set(SPOKEN_LINE_IDS);
const LOOP_NAMES: ReadonlySet<string> = new Set(LOOP_SOUNDS);

/**
 * Splits `hannu-swipe.2.mp3` into its cue name and take number. The take is
 * optional and the extension is ignored - the browser sniffs the real format
 * out of the bytes, not out of the name.
 */
export function parseSampleFileName(fileName: string): { name: string; take: number } | null {
  const parts = fileName.split('.');
  if (parts.length < 2) return null;
  parts.pop(); // extension
  const name = parts.shift();
  if (!name) return null;
  if (parts.length === 0) return { name, take: 1 };
  if (parts.length > 1) return null;
  const take = Number(parts[0]);
  if (!Number.isInteger(take) || take < 1) return null;
  return { name, take };
}

/**
 * Turns `{ path: url }` maps into the cue index. Pure, so the naming rules are
 * testable without a bundler and without any actual audio.
 */
export function buildSampleIndex(
  sfx: Record<string, string>,
  voice: Record<string, string>,
  loops: Record<string, string> = {},
): SampleIndex {
  const byId = new Map<SampleId, SampleSpec[]>();
  const unknown: string[] = [];

  /** Each folder knows which names are legal in it, and nothing else is. */
  const collect = (files: Record<string, string>, legal: ReadonlySet<string>, prefix = ''): void => {
    for (const path of Object.keys(files).sort()) {
      const fileName = path.split('/').pop() ?? path;
      const parsed = parseSampleFileName(fileName);
      if (!parsed || !legal.has(parsed.name)) {
        unknown.push(path);
        continue;
      }
      const id = `${prefix}${parsed.name}` as SampleId;
      const takes = byId.get(id) ?? [];
      takes.push({ id, take: parsed.take, url: files[path] });
      byId.set(id, takes);
    }
  };

  collect(sfx, ONE_SHOT_NAMES);
  collect(voice, VOICE_NAMES, 'voice:');
  collect(loops, LOOP_NAMES);
  for (const takes of byId.values()) takes.sort((a, b) => a.take - b.take);
  return { byId, unknown };
}

/* ------------------------------------------------------------ the folder -- */

// Eager + `?url`: the URLs are needed the instant the page loads, and they are
// strings, so this costs nothing beyond the import map itself. An empty folder
// yields an empty object - which is the state the game shipped in.
//
// The patterns have to be literals: Vite resolves these at build time, so an
// interpolated extension list would silently match nothing.
const sfxFiles = import.meta.glob('./samples/sfx/*.{mp3,ogg,wav,m4a,opus,webm}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const voiceFiles = import.meta.glob('./samples/voice/*.{mp3,ogg,wav,m4a,opus,webm}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const loopFiles = import.meta.glob('./samples/loops/*.{mp3,ogg,wav,m4a,opus,webm}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const index = buildSampleIndex(sfxFiles, voiceFiles, loopFiles);

/** Every take of every recorded cue, ready to be preloaded. */
export function allSamples(): SampleSpec[] {
  return [...index.byId.values()].flat();
}

export function samplesFor(id: SampleId): readonly SampleSpec[] {
  return index.byId.get(id) ?? [];
}

/**
 * Files sitting in the folder that no cue will ever play. Asserted empty by
 * samples.test.ts, and warned about at startup so it is visible either way.
 */
export function unknownSamples(): readonly string[] {
  return index.unknown;
}
